import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api, { formatApiError, BACKEND_URL } from "@/lib/api";
import { IDS } from "@/lib/testIds";
import { toast } from "sonner";
import { queueEntry, uuid } from "@/lib/offline";
import { ArrowLeft, FloppyDisk, WifiSlash, MapPin, Package as PackageIcon, QrCode, PencilSimple } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";
import { canEditItems } from "@/lib/roles";
import EditItemForm from "@/components/EditItemForm";

export default function ItemDetail() {
  const { variantId } = useParams();
  const [sp] = useSearchParams();
  const preferLocation = sp.get("loc") || "";
  const openEditFromUrl = sp.get("edit") === "1";
  const { user } = useAuth();
  const canEdit = canEditItems(user);
  const [editing, setEditing] = useState(openEditFromUrl && canEdit);

  const [data, setData] = useState(null);
  const [session, setSession] = useState(null);
  const [unit, setUnit] = useState("unit");
  const [qty, setQty] = useState("");
  const [mUnits, setMUnits] = useState("");
  const [mPacks, setMPacks] = useState("");
  const [mCases, setMCases] = useState("");
  const [locId, setLocId] = useState(preferLocation);
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("counted");
  const [saving, setSaving] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  useEffect(() => {
    api.get(`/items/${variantId}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)));
    // find active session
    api.get("/inventory/sessions?status=in_progress").then((r) => {
      if (r.data?.length) setSession(r.data[0]);
    });
  }, [variantId]);

  // Default location once data loads (does not react to user's later locId changes).
  useEffect(() => {
    if (!data?.locations?.length) return;
    setLocId((prev) => prev || data.locations[0].location_id);
  }, [data]);

  const variant = data?.variant;
  const product = data?.product;
  const locations = useMemo(() => data?.locations || [], [data]);
  const currentLoc = useMemo(() => locations.find((l) => l.location_id === locId), [locations, locId]);

  const normalizedPreview = useMemo(() => {
    const upp = Number(variant?.units_per_pack || 1);
    const ppc = Number(variant?.packs_per_case || 1);
    if (unit === "unit") return Number(qty || 0);
    if (unit === "pack") return Number(qty || 0) * upp;
    if (unit === "case") return Number(qty || 0) * upp * ppc;
    if (unit === "mixed")
      return Number(mUnits || 0) + Number(mPacks || 0) * upp + Number(mCases || 0) * upp * ppc;
    return 0;
  }, [unit, qty, mUnits, mPacks, mCases, variant]);

  const belowPar = currentLoc && (currentLoc.current_qty || 0) < (currentLoc.par_level || 0);

  const save = async () => {
    if (!session) {
      toast.error("No active session. Start one from the dashboard.");
      return;
    }
    if (unit !== "mixed" && Number(qty) <= 0 && status === "counted") {
      toast.error("Enter a quantity greater than zero");
      return;
    }
    setSaving(true);
    const payload = {
      client_uuid: uuid(),
      variant_id: variantId,
      location_id: locId || null,
      original_qty: unit === "mixed" ? 0 : Number(qty || 0),
      original_unit: unit,
      packs: Number(mPacks || 0),
      cases: Number(mCases || 0),
      units: Number(mUnits || 0),
      notes,
      status,
    };
    try {
      if (!online) {
        await queueEntry(session.session_id, payload);
        toast.success("Saved offline — will sync on reconnect");
      } else {
        await api.post(`/inventory/sessions/${session.session_id}/entries`, payload);
        toast.success("Entry saved");
      }
      setQty(""); setMUnits(""); setMPacks(""); setMCases("");
    } catch (e) {
      // On network failure, queue offline
      await queueEntry(session.session_id, payload);
      toast.warning("Saved offline (network error) — will retry");
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <div className="p-6 text-sm text-muted-foreground font-mono">Loading item…</div>;

  return (
    <div className="p-4 md:p-6 max-w-4xl">
      <Link to="/scan" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary mb-4">
        <ArrowLeft size={14} /> Back to scan
      </Link>

      <div className="grid md:grid-cols-[2fr_1fr] gap-4">
        {/* Left: identity & counters */}
        <div>
          <div className="card-flat border-border p-4 mb-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="label-tech mb-1">{product?.category || "Item"}</div>
                <h1 data-testid={IDS.itemDetailName} className="font-display font-black text-2xl md:text-3xl tracking-tight break-words">
                  {variant.product_name}
                </h1>
                <div className="text-xs font-mono text-muted-foreground mt-2 space-y-0.5">
                  <div data-testid="detail-business-pid">
                    PRODUCT ID · {product?.business_product_id && String(product.business_product_id).trim()
                      ? product.business_product_id
                      : <span className="italic">Missing</span>}
                  </div>
                  <div data-testid="detail-internal-id">INTERNAL ID · {variant.product_id}</div>
                  <div>VARIANT · {variant.variant_id}</div>
                  <div>PACK · {variant.pack_type} · {variant.units_per_pack} {variant.unit_size}</div>
                </div>
              </div>
              <div className="shrink-0 border border-border p-2 bg-white">
                <img
                  data-testid={IDS.itemDetailQr}
                  alt="QR"
                  src={`${BACKEND_URL}/api/qr/png/${encodeURIComponent(variant.qr_value || variant.variant_id)}?size=180`}
                  className="w-24 h-24 md:w-28 md:h-28"
                />
                <div className="text-[9px] font-mono text-center mt-1 text-black">{variant.qr_value || variant.variant_id}</div>
              </div>
            </div>
          </div>

          {/* Edit toggle (Admin + Supervisor only) */}
          {canEdit && !editing && (
            <div className="flex justify-end mb-3">
              <button
                data-testid="edit-item-btn"
                onClick={() => setEditing(true)}
                className="btn-hard h-10 px-4 bg-primary text-primary-foreground text-xs font-bold uppercase tracking-widest rounded-sm hover:bg-orange-600 flex items-center gap-2"
              >
                <PencilSimple size={14} weight="bold" /> Edit Item Details
              </button>
            </div>
          )}
          {editing && canEdit && (
            <EditItemForm
              variant={variant}
              product={product}
              locations={locations}
              categories={[]}
              onSaved={async () => {
                setEditing(false);
                const r = await api.get(`/items/${variantId}`);
                setData(r.data);
              }}
              onCancel={() => setEditing(false)}
            />
          )}

          {/* Location + on-hand */}
          <div className="card-flat border-border p-4 mb-4">
            <div className="flex items-center justify-between mb-3">
              <div className="label-tech">Location & On-Hand</div>
              {belowPar && (
                <span className="text-[10px] font-bold tracking-[0.2em] uppercase bg-destructive text-destructive-foreground px-2 py-0.5">
                  Below PAR
                </span>
              )}
            </div>
            {locations.length > 1 && (
              <select
                value={locId}
                onChange={(e) => setLocId(e.target.value)}
                className="w-full h-10 px-2 border border-border bg-background rounded-sm text-sm mb-3"
              >
                {locations.map((l) => (
                  <option key={l.location_id} value={l.location_id}>{l.location_full}</option>
                ))}
              </select>
            )}
            {currentLoc ? (
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <div className="label-tech mb-1">On Hand</div>
                  <div className="font-display font-black text-2xl">{currentLoc.current_qty || 0}</div>
                </div>
                <div>
                  <div className="label-tech mb-1">PAR</div>
                  <div className="font-display font-black text-2xl">{currentLoc.par_level || variant.par_level || 0}</div>
                </div>
                <div>
                  <div className="label-tech mb-1">Location</div>
                  <div className="font-mono text-sm mt-1 flex items-center gap-1"><MapPin size={12} />{currentLoc.location_full}</div>
                </div>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">No location assignment yet.</div>
            )}
          </div>

          {/* Quantity entry */}
          <div className="card-flat border-border p-4">
            <div className="label-tech mb-3">Enter Count</div>

            {/* Unit selector */}
            <div className="grid grid-cols-4 gap-1 mb-3" data-testid={IDS.qtyUnit}>
              {[
                { k: "unit", label: variant.base_unit || "UNIT" },
                { k: "pack", label: "PACK" },
                { k: "case", label: "CASE" },
                { k: "mixed", label: "MIXED" },
              ].map((o) => (
                <button
                  key={o.k}
                  onClick={() => setUnit(o.k)}
                  className={`h-10 border rounded-sm text-[11px] font-bold uppercase tracking-widest btn-hard ${
                    unit === o.k
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border hover:bg-muted"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>

            {unit !== "mixed" ? (
              <input
                data-testid={IDS.qtyOriginal}
                type="number"
                min="0"
                step="0.01"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                placeholder={`Quantity in ${unit}`}
                className="w-full h-14 px-4 border border-border bg-background rounded-sm text-2xl font-display font-black outline-none focus:ring-2 focus:ring-primary mb-3"
              />
            ) : (
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div>
                  <div className="label-tech mb-1">Units</div>
                  <input
                    data-testid={IDS.qtyMixedUnits}
                    type="number" min="0" value={mUnits}
                    onChange={(e) => setMUnits(e.target.value)}
                    className="w-full h-12 px-3 border border-border bg-background rounded-sm text-lg font-display font-bold outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <div className="label-tech mb-1">Packs</div>
                  <input
                    data-testid={IDS.qtyMixedPacks}
                    type="number" min="0" value={mPacks}
                    onChange={(e) => setMPacks(e.target.value)}
                    className="w-full h-12 px-3 border border-border bg-background rounded-sm text-lg font-display font-bold outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <div className="label-tech mb-1">Cases</div>
                  <input
                    data-testid={IDS.qtyMixedCases}
                    type="number" min="0" value={mCases}
                    onChange={(e) => setMCases(e.target.value)}
                    className="w-full h-12 px-3 border border-border bg-background rounded-sm text-lg font-display font-bold outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 mb-3">
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="h-10 px-2 border border-border bg-background rounded-sm text-sm"
              >
                <option value="counted">Counted</option>
                <option value="out_of_stock">Out of Stock</option>
                <option value="damaged">Damaged</option>
                <option value="expired">Expired</option>
                <option value="unavailable">Unavailable</option>
                <option value="not_found">Not Found</option>
              </select>
              <div className="text-right">
                <div className="label-tech">Normalized</div>
                <div className="font-display font-black text-lg">
                  {normalizedPreview.toFixed(2)} <span className="text-xs text-muted-foreground">{variant.base_unit}</span>
                </div>
              </div>
            </div>

            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notes (optional)"
              className="w-full px-3 py-2 border border-border bg-background rounded-sm text-sm outline-none focus:ring-2 focus:ring-primary mb-3 font-mono"
            />

            {!session && (
              <div className="border border-warning/60 bg-warning/10 text-yellow-700 dark:text-yellow-400 px-3 py-2 text-xs rounded-sm mb-3">
                No active session. <Link to="/" className="underline">Start one</Link> to save counts.
              </div>
            )}

            <button
              data-testid={IDS.saveEntry}
              onClick={save}
              disabled={saving || !session}
              className="btn-hard w-full h-12 bg-primary text-primary-foreground font-bold uppercase tracking-widest text-xs rounded-sm hover:bg-orange-600 hover:text-white disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {!online && <WifiSlash size={16} weight="bold" />}
              <FloppyDisk size={16} weight="bold" />
              {(() => {
                if (saving) return "Saving…";
                if (!online) return "Save Offline";
                return "Save Entry";
              })()}
            </button>
          </div>
        </div>

        {/* Right: meta */}
        <div className="space-y-4">
          <div className="card-flat border-border p-4">
            <div className="label-tech mb-2">Pack Configuration</div>
            <div className="text-sm space-y-1 font-mono">
              <div className="flex justify-between"><span className="text-muted-foreground">Pack type</span><span>{variant.pack_type}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Unit size</span><span>{variant.unit_size}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Units / pack</span><span>{variant.units_per_pack}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Packs / case</span><span>{variant.packs_per_case}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Units / case</span><span>{variant.units_per_case}</span></div>
            </div>
          </div>
          <div className="card-flat border-border p-4">
            <div className="label-tech mb-2">PAR & Reorder</div>
            <div className="text-sm space-y-1 font-mono">
              <div className="flex justify-between"><span className="text-muted-foreground">PAR level</span><span>{variant.par_level}</span></div>
              <div className="flex justify-between" data-testid="item-cost-view">
                <span className="text-muted-foreground">Cost <span className="text-[10px]">(pack)</span></span>
                {variant.cost === null || variant.cost === undefined || variant.cost === "" ? (
                  <span className="italic text-muted-foreground">Not Set</span>
                ) : (
                  <span className="font-mono">
                    ${Number(variant.cost).toFixed(2)}
                    {Number(variant.cost) > 0 && Number(variant.units_per_pack) > 0 && (
                      <span className="text-[10px] text-muted-foreground ml-2">
                        (Unit ${(Number(variant.cost) / Number(variant.units_per_pack)).toFixed(4)})
                      </span>
                    )}
                  </span>
                )}
              </div>
              <div className="flex justify-between"><span className="text-muted-foreground">Reorder point</span><span>{variant.reorder_point}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Safety stock</span><span>{variant.safety_stock}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Min. order</span><span>{variant.min_order_quantity}</span></div>
            </div>
          </div>
          {locations.length > 0 && (
            <div className="card-flat border-border p-4">
              <div className="label-tech mb-2">All Locations</div>
              <div className="space-y-1 text-xs font-mono">
                {locations.map((l) => (
                  <div key={l.location_id} className="flex justify-between">
                    <span>{l.location_full}</span>
                    <span className={l.current_qty >= (l.par_level || 0) ? "text-success" : "text-destructive"}>
                      {l.current_qty || 0} / {l.par_level || 0}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
